import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isLocale, type Locale } from '@autodialer/shared';

import { formatMoney } from '../format';
import { de, type Dictionary, type TranslationKey } from './de';
import { en } from './en';
import { tr } from './tr';

/**
 * Sehr schlanke Mehrsprachigkeit ohne zusätzliche Bibliothek.
 *
 * Regeln aus 02_navigation_ux_tutorial_i18n.md:
 * - Sprachen DE, EN, TR
 * - alle sichtbaren Texte aus Sprachdateien
 * - keine automatische Übersetzung zur Laufzeit
 *
 * Die Sprache eines angemeldeten Kontos wird serverseitig gespeichert. Vor der
 * Anmeldung merkt sich der Browser die zuletzt gewählte Sprache.
 */

const dictionaries: Record<Locale, Dictionary> = { de, en, tr };

const STORAGE_KEY = 'autodialer.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Platzhalter wie {count} werden aus `params` ersetzt. */
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  /**
   * Übersetzt einen Fehlercode des Servers in Alltagssprache.
   *
   * `details` sind die Angaben, die der Server mitschickt - etwa der
   * geforderte Mindestbetrag. Geldbeträge darin werden für die Anzeige
   * formatiert, damit in der Meldung "25,00 €" steht und nicht "25.00000".
   */
  tError: (code: string | undefined, details?: Record<string, unknown>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) {
      return stored;
    }
    const browser = window.navigator.language.slice(0, 2);
    if (isLocale(browser)) {
      return browser;
    }
  } catch {
    // Kein Zugriff auf den Speicher - dann eben die Standardsprache.
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ohne Speicher gilt die Sprache nur für dieses Fenster.
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dictionary = dictionaries[locale];
    const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
      const text = dictionary[key] ?? de[key] ?? key;
      if (!params) {
        return text;
      }
      return text.replace(/\{(\w+)\}/g, (match, name: string) =>
        params[name] === undefined ? match : String(params[name]),
      );
    };

    return {
      locale,
      setLocale,
      t,
      tError: (code, details) => {
        if (!code) {
          return t('error.unknown');
        }
        const key = `error.${code}` as TranslationKey;
        if (!dictionary[key]) {
          return t('error.unknown');
        }
        return t(key, details ? displayValues(details) : undefined);
      },
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Bereitet die Angaben einer Fehlermeldung für die Anzeige auf.
 *
 * Alles, was wie ein Dezimalbetrag aussieht, wird als Geldbetrag formatiert.
 * Gerechnet wird damit nicht - das passiert ausschließlich serverseitig.
 */
function displayValues(details: Record<string, unknown>): Record<string, string> {
  const werte: Record<string, string> = {};
  for (const [name, wert] of Object.entries(details)) {
    werte[name] =
      typeof wert === 'string' && /^-?\d+(\.\d+)?$/.test(wert) ? formatMoney(wert) : String(wert);
  }
  return werte;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n muss innerhalb des I18nProvider verwendet werden.');
  }
  return value;
}

export { SUPPORTED_LOCALES };
export type { Locale, TranslationKey };
